import { useCallback, useEffect, useRef } from 'react';
import {
    DEFAULT_VIEWPORT,
    MIN_ZOOM_PERCENT,
    MAX_ZOOM_PERCENT,
    CTRL_WHEEL_ZOOM_SENSITIVITY,
    CTRL_WHEEL_MAX_DELTA_PER_FRAME,
} from '../constants/canvas';
import { getNodeAbsolutePosition, getNodeSize } from '../utils/graph';

/**
 * 画布视口/缩放管理
 */
export const useCanvasViewport = ({
    canvasRef,
    reactFlowRef,
    nodesRef,
    edgesRef,
    setZoom,
    setViewportInteracting,
    isPmView,
    activeId,
    persistCanvasBatch,
    canvasEmpty,
    nodes,
}) => {
    const viewportRef = useRef(DEFAULT_VIEWPORT);
    const persistedViewportRef = useRef(DEFAULT_VIEWPORT);
    const viewportPersistTimerRef = useRef(null);
    const ctrlWheelDeltaRef = useRef(0);
    const ctrlWheelFrameRef = useRef(null);

    // 重置画布视口到默认位置和缩放
    const resetCanvasViewport = useCallback(() => {
        viewportRef.current = { ...DEFAULT_VIEWPORT };
        persistedViewportRef.current = { ...DEFAULT_VIEWPORT };
        reactFlowRef.current?.setViewport?.(DEFAULT_VIEWPORT, { duration: 0 });
        setZoom(100);
    }, [reactFlowRef, setZoom]);

    // 延迟保存视口变化，避免拖动画布时频繁请求
    const scheduleViewportPersist = useCallback((nextViewport) => {
        viewportRef.current = { ...nextViewport };
        setZoom(Math.round(nextViewport.zoom * 100));

        if (isPmView) return;
        if (!activeId || nodesRef.current.length === 0) return;

        if (viewportPersistTimerRef.current) {
            clearTimeout(viewportPersistTimerRef.current);
        }

        viewportPersistTimerRef.current = setTimeout(() => {
            const beforeViewport = persistedViewportRef.current;
            const afterViewport = viewportRef.current;
            const changed = (
                beforeViewport.x !== afterViewport.x
                || beforeViewport.y !== afterViewport.y
                || beforeViewport.zoom !== afterViewport.zoom
            );

            if (!changed) return;

            persistCanvasBatch({
                beforeNodes: nodesRef.current,
                beforeEdges: edgesRef.current,
                afterNodes: nodesRef.current,
                afterEdges: edgesRef.current,
                beforeViewport,
                afterViewport,
            });
        }, 240);
    }, [activeId, edgesRef, isPmView, persistCanvasBatch, nodesRef, setZoom]);

    // 调整画布缩放比例
    const handleZoomChange = useCallback((nextZoom) => {
        if (canvasEmpty) return;

        const clampedZoom = Math.max(MIN_ZOOM_PERCENT, Math.min(MAX_ZOOM_PERCENT, nextZoom));
        setZoom(clampedZoom);
        reactFlowRef.current?.zoomTo(clampedZoom / 100, { duration: 150 });
    }, [canvasEmpty, reactFlowRef, setZoom]);

    // 将当前画布内容调整到合适视野范围
    const handleFitCanvas = useCallback(() => {
        const reactFlow = reactFlowRef.current;
        const canvas = canvasRef.current;
        if (!reactFlow || !canvas || nodes.length === 0) return;

        const nodeMap = new Map(nodes.map((node) => [node.id, node]));
        const contentBounds = nodes.reduce((acc, node) => {
            const absolutePosition = getNodeAbsolutePosition(node, nodeMap);
            const { width, height } = getNodeSize(node);

            return {
                minX: Math.min(acc.minX, absolutePosition.x),
                minY: Math.min(acc.minY, absolutePosition.y),
                maxX: Math.max(acc.maxX, absolutePosition.x + width),
                maxY: Math.max(acc.maxY, absolutePosition.y + height),
            };
        }, {
            minX: Number.POSITIVE_INFINITY,
            minY: Number.POSITIVE_INFINITY,
            maxX: Number.NEGATIVE_INFINITY,
            maxY: Number.NEGATIVE_INFINITY,
        });

        const canvasBounds = canvas.getBoundingClientRect();
        const screenTopLeft = reactFlow.flowToScreenPosition({
            x: contentBounds.minX,
            y: contentBounds.minY,
        });
        const screenBottomRight = reactFlow.flowToScreenPosition({
            x: contentBounds.maxX,
            y: contentBounds.maxY,
        });
        const viewportPadding = 12;
        const fullyVisible = (
            screenTopLeft.x >= canvasBounds.left + viewportPadding
            && screenTopLeft.y >= canvasBounds.top + viewportPadding
            && screenBottomRight.x <= canvasBounds.right - viewportPadding
            && screenBottomRight.y <= canvasBounds.bottom - viewportPadding
        );

        if (fullyVisible) {
            setZoom(Math.round(reactFlow.getZoom() * 100));
            return;
        }

        reactFlow.fitView({ duration: 200, padding: 0.24 });
        setTimeout(() => {
            setZoom(Math.round(reactFlow.getZoom() * 100));
        }, 220);
    }, [nodes, reactFlowRef, canvasRef, setZoom]);

    // 移动过程中只同步 ref，避免滚轮每一帧都触发整张画布重渲染
    const handleMove = useCallback((event, viewport) => {
        viewportRef.current = { ...viewport };
        setViewportInteracting((prev) => (prev ? prev : true));
    }, [setViewportInteracting]);

    // 交互结束后再刷新缩放展示并持久化最终视口
    const handleMoveEnd = useCallback((event, viewport) => {
        setViewportInteracting(false);
        scheduleViewportPersist(viewport);
    }, [scheduleViewportPersist, setViewportInteracting]);

    // 初始化 ReactFlow 实例引用
    const handleInit = useCallback((reactFlowInstance) => {
        reactFlowRef.current = reactFlowInstance;
        setZoom(Math.round(reactFlowInstance.getZoom() * 100));
    }, [reactFlowRef, setZoom]);

    // 单独接管 Ctrl/Command + 滚轮，限制缩放幅度并保持鼠标位置不漂移
    useEffect(() => {
        const canvasElement = canvasRef.current;
        if (!canvasElement) return undefined;

        const handleCtrlWheel = (event) => {
            if (!event.ctrlKey && !event.metaKey) return;

            event.preventDefault();
            event.stopPropagation();

            const deltaModeScale = event.deltaMode === 1
                ? 16
                : event.deltaMode === 2
                    ? canvasElement.clientHeight
                    : 1;
            ctrlWheelDeltaRef.current += event.deltaY * deltaModeScale;

            if (ctrlWheelFrameRef.current) return;
            ctrlWheelFrameRef.current = requestAnimationFrame(() => {
                ctrlWheelFrameRef.current = null;
                const reactFlow = reactFlowRef.current;
                if (!reactFlow) {
                    ctrlWheelDeltaRef.current = 0;
                    return;
                }

                const normalizedDelta = Math.max(
                    -CTRL_WHEEL_MAX_DELTA_PER_FRAME,
                    Math.min(CTRL_WHEEL_MAX_DELTA_PER_FRAME, ctrlWheelDeltaRef.current),
                );
                ctrlWheelDeltaRef.current = 0;

                const currentViewport = reactFlow.getViewport?.() || viewportRef.current || DEFAULT_VIEWPORT;
                const currentZoom = currentViewport.zoom || 1;
                const nextZoom = Math.max(
                    MIN_ZOOM_PERCENT / 100,
                    Math.min(
                        MAX_ZOOM_PERCENT / 100,
                        currentZoom * Math.exp(-normalizedDelta * CTRL_WHEEL_ZOOM_SENSITIVITY),
                    ),
                );
                if (Math.abs(nextZoom - currentZoom) < 0.0001) return;

                const canvasRect = canvasElement.getBoundingClientRect();
                const pointerX = event.clientX - canvasRect.left;
                const pointerY = event.clientY - canvasRect.top;
                const flowX = (pointerX - currentViewport.x) / currentZoom;
                const flowY = (pointerY - currentViewport.y) / currentZoom;
                const nextViewport = {
                    x: pointerX - (flowX * nextZoom),
                    y: pointerY - (flowY * nextZoom),
                    zoom: nextZoom,
                };

                reactFlow.setViewport?.(nextViewport, { duration: 0 });
            });
        };

        canvasElement.addEventListener('wheel', handleCtrlWheel, { passive: false, capture: true });

        return () => {
            canvasElement.removeEventListener('wheel', handleCtrlWheel, { capture: true });
            if (ctrlWheelFrameRef.current) {
                cancelAnimationFrame(ctrlWheelFrameRef.current);
                ctrlWheelFrameRef.current = null;
            }
            ctrlWheelDeltaRef.current = 0;
        };
    }, [canvasRef, reactFlowRef]);

    return {
        viewportRef,
        persistedViewportRef,
        viewportPersistTimerRef,
        resetCanvasViewport,
        scheduleViewportPersist,
        handleZoomChange,
        handleMove,
        handleMoveEnd,
        handleFitCanvas,
        handleInit,
    };
};